import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldCheck, MessageSquare, Smartphone, Mail, CheckCircle2, Building2, Globe, Lock } from 'lucide-react';

const API_BASE = 'https://warngen-api-352514293915.europe-west1.run.app';

export default function PaginaCanalOficial() {
    const { slug } = useParams();
    const [canal, setCanal] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        fetch(`${API_BASE}/api/public/canal/${encodeURIComponent(slug || '')}`)
            .then(r => r.json())
            .then(d => {
                if (d.success && d.data) {
                    setCanal(d.data);
                    document.title = `${d.data.nombre} — Canal Oficial de Comunicaciones y Alertas`;
                } else {
                    setError(true);
                }
                setCargando(false);
            })
            .catch(() => {
                setError(true);
                setCargando(false);
            });
    }, [slug]);

    if (cargando) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
                <div className="text-slate-500 text-sm font-medium animate-pulse">
                    Cargando información del canal oficial...
                </div>
            </div>
        );
    }

    if (error || !canal) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
                <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center space-y-3 border border-slate-200">
                    <ShieldCheck className="mx-auto text-slate-400" size={44} />
                    <h1 className="text-xl font-bold text-slate-800">Canal no encontrado</h1>
                    <p className="text-sm text-slate-500">
                        El identificador de canal solicitado no se encuentra activo en el registro oficial.
                    </p>
                </div>
            </div>
        );
    }

    const color = canal.color_tema || '#1E3A8A';
    const waNumero = (canal.whatsapp_number || '').replace('whatsapp:', '').trim();
    const smsRemitente = canal.sms_number || canal.sms_sender_id || '';
    const emailRemitente = canal.email_remitente || 'alertas@mail.warngen.es';

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col justify-between text-slate-800">
            {/* Barra superior institucional */}
            <div className="bg-slate-900 text-slate-200 text-xs py-2.5 px-4 border-b border-slate-800">
                <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 font-medium">
                        <ShieldCheck size={15} className="text-emerald-400" />
                        Registro Oficial de Canales de Comunicación y Alertas a la Población
                    </span>
                    <span className="text-slate-400 font-mono text-[11px]">
                        REF: {canal.codigo}
                    </span>
                </div>
            </div>

            {/* Contenido principal */}
            <main className="max-w-4xl w-full mx-auto px-4 py-10 space-y-8">
                {/* Cabecera de marca con el Display Name exacto */}
                <section className="bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden">
                    <div className="h-3" style={{ backgroundColor: color }} />
                    <div className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                        <div className="flex items-center gap-4">
                            <div
                                className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-extrabold text-2xl shadow-md shrink-0"
                                style={{ backgroundColor: color }}
                            >
                                {canal.nombre.substring(0, 2).toUpperCase()}
                            </div>
                            <div>
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 mb-1.5">
                                    <CheckCircle2 size={13} /> Canal Oficial Verificado
                                </div>
                                <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                                    {canal.nombre}
                                </h1>
                                <p className="text-sm text-slate-500 mt-0.5">
                                    Servicio Oficial de Avisos, Notificaciones y Alertas Ciudadanas / Corporativas
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="px-6 sm:px-8 pb-6 sm:pb-8 pt-2 border-t border-slate-100 space-y-4">
                        <p className="text-sm text-slate-600 leading-relaxed">
                            {canal.descripcion ||
                                `${canal.nombre} es un canal oficial de comunicación directa destinado al envío de avisos informativos, notificaciones urgentes y alertas de interés para los usuarios y ciudadanos suscritos al servicio.`}
                        </p>

                        {/* Remitentes autorizados del canal */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 mb-1">
                                    <Smartphone size={15} className="text-green-600" />
                                    WhatsApp Oficial
                                </div>
                                <div className="font-bold text-slate-900 text-sm">
                                    {canal.nombre}
                                </div>
                                {waNumero && (
                                    <div className="text-xs font-mono text-slate-500 mt-0.5">
                                        {waNumero}
                                    </div>
                                )}
                            </div>

                            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 mb-1">
                                    <MessageSquare size={15} className="text-amber-600" />
                                    Remitente SMS
                                </div>
                                <div className="font-bold text-slate-900 text-sm font-mono">
                                    {smsRemitente || canal.nombre}
                                </div>
                                {canal.sms_sender_id && canal.sms_number && (
                                    <div className="text-xs text-slate-500 mt-0.5">
                                        Alias: {canal.sms_sender_id}
                                    </div>
                                )}
                            </div>

                            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 mb-1">
                                    <Mail size={15} className="text-blue-600" />
                                    Correo Electrónico
                                </div>
                                <div className="font-bold text-slate-900 text-sm truncate" title={emailRemitente}>
                                    {canal.email_nombre || canal.nombre}
                                </div>
                                <div className="text-xs font-mono text-slate-500 truncate mt-0.5">
                                    {emailRemitente}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Declaración explícita de vinculación empresarial para revisión de Meta (WhatsApp Business Display Name) */}
                <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8 space-y-4">
                    <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                        <Building2 size={18} className="text-slate-700" />
                        Titularidad y Operación Tecnológica del Canal
                    </h2>
                    <p className="text-sm text-slate-600 leading-relaxed">
                        El canal de comunicación y mensajería <strong>{canal.nombre}</strong> (código identificador <code className="px-1.5 py-0.5 bg-slate-100 rounded text-xs font-mono">{canal.codigo}</code>) es un servicio operado, gestionado y provisto tecnológicamente por <strong>Desarrollo Online Ecosystem</strong> a través de su plataforma oficial de difusión de avisos y alertas <strong>WarnGen</strong>.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 text-xs text-slate-600">
                        <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
                            <div className="font-semibold text-slate-800">Nombre a mostrar en WhatsApp (Display Name):</div>
                            <div className="font-mono text-sm font-bold text-slate-900">{canal.nombre}</div>
                            <div className="text-slate-500">Cuenta oficial vinculada al portafolio empresarial verificado.</div>
                        </div>
                        <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
                            <div className="font-semibold text-slate-800">Entidad Operadora y Titular del Portafolio:</div>
                            <div className="font-mono text-sm font-bold text-slate-900">Desarrollo Online Ecosystem</div>
                            <div className="text-slate-500">Proveedor oficial de infraestructura de mensajería (WarnGen).</div>
                        </div>
                    </div>
                </section>

                {/* Protección de datos y consentimiento */}
                <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-2 text-xs text-slate-600">
                    <div className="font-bold text-slate-800 flex items-center gap-1.5 text-sm">
                        <Lock size={15} className="text-slate-600" />
                        Privacidad, Consentimiento (Opt-in) y Baja del Servicio
                    </div>
                    <p className="leading-relaxed">
                        Las comunicaciones enviadas desde <strong>{canal.nombre}</strong> se realizan exclusivamente a usuarios que han otorgado su consentimiento previo o que forman parte de los directorios de avisos autorizados conforme al Reglamento General de Protección de Datos (RGPD). En cualquier momento puede solicitar la baja respondiendo <strong>BAJA</strong> o <strong>STOP</strong> por el mismo canal.
                    </p>
                </section>
            </main>

            {/* Footer legal con mención clara de la entidad verificada en Meta */}
            <footer className="bg-white border-t border-slate-200 py-6 px-4 text-xs text-slate-500">
                <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
                    <div>
                        <strong>{canal.nombre}</strong> — Canal oficial operado por <strong>Desarrollo Online Ecosystem</strong> (Plataforma WarnGen).
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-400">
                        <Globe size={13} />
                        <span>warngen.es</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
